import { computed, createEnvironmentInjector, EnvironmentInjector, inject, Injectable, InjectionToken, isDevMode, Provider, runInInjectionContext, signal, Signal, Type } from '@angular/core';

import { signalMethod } from '@ngrx/signals';

import { AsyncRegistryInitializer, provideServicesRegistry, resolveRegistryEntry, ServicesRegistry } from '@kros-sk/core/registry';

import { SCHEMA_STATE } from '../address/schema-state';
import { SchemaDataSourceHost } from '../data-source/schema-data-source';
import { SchemaActionDeclaration, SchemaActionsConfig, SchemaActionsMap } from '../model/schema-action.types';
import { SCHEMA_CONTEXT } from '../model/schema-context';

/**
 * What an action receives. An object rather than positional arguments because what else it should
 * carry — the trigger, an abort signal — is deliberately still open: adding a field here breaks no
 * registered action, adding a parameter would break every one of them.
 */
export interface SchemaActionContext<TConfig = unknown, TPayload = unknown> {
  /**
   * The action's own `config`, exactly as the schema declared it — and `undefined` when it declared
   * none, which `SchemaActionDeclaration.config` allows. An action that needs one says so by checking,
   * because nothing upstream can: the renderer never looks inside a config.
   */
  readonly config: TConfig | undefined;

  /**
   * What the caller had at the moment it fired — a list row for a swipe, nothing for a tile click. It
   * is passed rather than read from the data source on purpose: a payload is not always the node's
   * data, and a runner that guessed would be wrong for every trigger that carries something else.
   */
  readonly payload: TPayload | undefined;
}

/**
 * A named capability an application registers. The renderer resolves the key and runs it; it knows
 * nothing about what any action does, which is why the registry ships empty.
 *
 * Always async, so a caller can await either kind: a synchronous action returns an already-resolved
 * promise, and a failure is a rejection rather than a silent nothing.
 *
 * Two constraints on an implementation, both because an instance is built per invocation:
 * dependencies come from `inject()`, never constructor parameters (the repo rule anyway), and the
 * class holds no state between runs — anything that must survive belongs in a service it injects.
 * Its `DestroyRef` belongs to the run, too: whatever it registers there is torn down when the run ends.
 */
export interface SchemaAction<TConfig = unknown, TPayload = unknown> {
  execute(context: SchemaActionContext<TConfig, TPayload>): Promise<void>;
}

export type SchemaActionEntry = AsyncRegistryInitializer<Type<SchemaAction>>;

export const SCHEMA_ACTIONS_REGISTRY = new InjectionToken<ServicesRegistry<SchemaAction>>('SCHEMA_ACTIONS_REGISTRY');

/**
 * Registers the actions reachable from this injector. Ships no built-ins: a reusable action class
 * lives in a library and the application registers it here, the way `RestApiSchemaDataSource` is
 * registered rather than provided by the renderer.
 */
export function provideSchemaActions(...actions: SchemaActionEntry[]): Provider {
  return provideServicesRegistry(SCHEMA_ACTIONS_REGISTRY, ...actions);
}

/**
 * Carries the action declarations down the tree. Every schema node hosts one, so most of them declare
 * nothing; a host that answered with only its own declarations would cut its subtree off from the
 * ones above it.
 *
 * It also carries the node's own `actionsMap`, which travels the opposite way: declarations are
 * inherited, a map is not. See {@link SchemaActionsMap} for why.
 */
@Injectable()
export class SchemaActionsHost {
  readonly #parent = inject(SchemaActionsHost, { skipSelf: true, optional: true });

  readonly #declarations = signal<SchemaActionsConfig | undefined>(undefined);

  readonly #map = signal<SchemaActionsMap | undefined>(undefined);

  /**
   * Merged by name, nearer wins — the same walk `Registry` does across injector levels.
   *
   * Annotated because the chain to the parent host makes the type self-referential, which TypeScript
   * cannot infer.
   */
  readonly declarations: Signal<SchemaActionsConfig> = computed(() => ({
    ...(this.#parent?.declarations() ?? {}),
    ...(this.#declarations() ?? {}),
  }));

  readonly registerDeclarations = signalMethod<SchemaActionsConfig | undefined>((value) => this.#declarations.set(value));

  readonly registerMap = signalMethod<SchemaActionsMap | undefined>((value) => this.#map.set(value));

  /**
   * The declaration names a fired slot resolves to. **This node's own map only** — never the parent's,
   * which is the whole difference between a map and a declaration.
   *
   * Unmapped falls through to the slot's own name, so every schema written before maps existed resolves
   * exactly as it did. A listed slot resolves to every name on its list, and an empty list to none.
   */
  declarationNames(slot: string): readonly string[] {
    const mapped = this.#map()?.[slot];

    if (mapped === undefined) {
      return [slot];
    }

    return typeof mapped === 'string' ? [mapped] : mapped;
  }
}

/**
 * How a component reaches the actions declared for its node. The name it passes is its **own word for
 * what happened** — a literal it owns, like the `'click'` in a template or a `SwipeDirection` — never a
 * value read out of a config. One per control, so a row with a swipe on each side and a form with submit
 * and cancel each name theirs separately.
 *
 * Which declaration that name lands on is the schema's business, not the component's: the node's
 * `actionsMap` may point a slot at a declaration of another name, and the component never learns of it.
 *
 * The name is required, never optional, so an unconfigured control cannot quietly fire whatever the
 * fallback was — there is no default name and no reserved one.
 */
export interface SchemaActionsRunner {
  /** Whether a named action is declared on this node or above it — a tile with none must not look clickable. */
  hasAction(name: string): boolean;

  /**
   * Fires one named action, optionally with whatever the caller had at that moment — the list row a
   * swipe was on, the id a menu item stands for. Resolves once the action has finished.
   */
  runAction(name: string, payload?: unknown): Promise<void>;
}

/**
 * The component decides *when* an action fires — click, swipe, change — and never touches the
 * registry or the config itself: the runner holds the inherited declarations and passes them on.
 */
export function injectSchemaActions(): SchemaActionsRunner {
  const host = inject(SchemaActionsHost);
  const registry = inject(SCHEMA_ACTIONS_REGISTRY, { optional: true });
  const injector = inject(EnvironmentInjector);

  /**
   * An action is built in the environment injector, which holds none of the element providers, so a context a
   * host publishes on a component would never reach it. Each run is handed the one this node reads, the way
   * `connectSchemaDataSource` hands it to a data source: `$$context.X$$` in an action reads what `$context.X`
   * reads in the node that fired it.
   */
  const context = inject(SCHEMA_CONTEXT, { optional: true });

  /**
   * What `$data`, `$state` and `$source` read in an action's config: the node that fired it (D11) — its state and the
   * source it lives in. Both are element providers of the node's scope, which the run cannot reach, so they travel
   * with the context.
   */
  const nodeState = inject(SCHEMA_STATE, { optional: true });
  const nodeSource = inject(SchemaDataSourceHost, { optional: true });

  /**
   * The one place a fired slot becomes declarations, so `hasAction` and `runAction` can never disagree
   * about it — a mapped control that fired correctly but rendered as inert would be the worse bug. A name
   * the slot lists but nothing declares comes back apart, for the warning.
   */
  const declarationsFor = (slot: string): { declared: SchemaActionDeclaration[]; undeclared: string[] } => {
    const declarations = host.declarations();
    const declared: SchemaActionDeclaration[] = [];
    const undeclared: string[] = [];

    for (const name of host.declarationNames(slot)) {
      const declaration = declarations[name];

      if (declaration) {
        declared.push(declaration);
      } else {
        undeclared.push(name);
      }
    }

    return { declared, undeclared };
  };

  /**
   * One declaration's run, as a single action has always run. The run's own injector, under the environment
   * one, holds the context, the state and the source of the node that fired it, and the run takes it down
   * when it ends: an action keeps nothing from one run to the next, so neither does what it registered.
   */
  const runOne = async (
    actionsRegistry: ServicesRegistry<SchemaAction>,
    declaration: SchemaActionDeclaration,
    payload: unknown
  ): Promise<void> => {
    const runInjector = createEnvironmentInjector(
      [
        ...(context ? [{ provide: SCHEMA_CONTEXT, useValue: context }] : []),
        ...(nodeState ? [{ provide: SCHEMA_STATE, useValue: nodeState }] : []),
        ...(nodeSource ? [{ provide: SchemaDataSourceHost, useValue: nodeSource }] : []),
      ],
      injector
    );

    try {
      /**
       * Resolution failing and the action failing are different kinds of failure. An unregistered key is
       * a schema-authoring bug, reported the way an unresolvable `componentType` is; whatever the
       * action's own `execute` throws is its business and reaches the caller that awaited it.
       */
      let action: SchemaAction;

      try {
        const actionType = await resolveRegistryEntry(actionsRegistry.registry, declaration.type);
        action = runInInjectionContext(runInjector, () => new actionType());
      } catch (failure) {
        reportResolveFailure(declaration.type, failure);

        return;
      }

      await action.execute({ config: declaration.config, payload });
    } finally {
      runInjector.destroy();
    }
  };

  /**
   * Every declaration the slot lists, all at once, the way a control that logs beside its own work behaves:
   * neither waits for the other. A failing one cannot stop the rest, which have already started, so the run
   * settles only once every one has, and then hands the caller the first failure in written order — the way
   * a single action's failure reaches it.
   */
  const fire = async (slot: string, payload: unknown): Promise<void> => {
    const { declared, undeclared } = declarationsFor(slot);

    undeclared.forEach((name) => reportMissingDeclaration(slot, name, Object.keys(host.declarations())));

    if (!declared.length) {
      return;
    }

    if (!registry) {
      reportMissingRegistry(declared.map(({ type }) => type).join(', '));

      return;
    }

    const outcomes = await Promise.allSettled(declared.map((declaration) => runOne(registry, declaration, payload)));
    const failure = outcomes.find((outcome): outcome is PromiseRejectedResult => outcome.status === 'rejected');

    if (failure) {
      throw failure.reason;
    }
  };

  return {
    hasAction: (name: string) => declarationsFor(name).declared.length > 0,
    runAction: (name: string, payload?: unknown) => fire(name, payload),
  };
}

/**
 * Both failures are schema-authoring bugs an end user can do nothing about, so in production nothing
 * happens beyond the action not running — the treatment an unresolvable `componentType` already gets.
 */
function reportMissingDeclaration(slot: string, target: string, available: string[]): void {
  if (isDevMode()) {
    const declared = available.length ? `Declared here: ${available.join(', ')}.` : 'No actions are declared on this node or above it.';

    /** Naming both is the difference between finding a typo in the map and hunting for one in the schema. */
    const mapped = target === slot ? '' : ` (this node's actionsMap points slot "${slot}" at it)`;

    console.warn(`Schema node fired action "${target}"${mapped}, which is not declared. ${declared}`);
  }
}

function reportMissingRegistry(type: string): void {
  if (isDevMode()) {
    console.warn(
      `Schema node declares action "${type}", but no SCHEMA_ACTIONS_REGISTRY is reachable from this injector, ` +
        `so nothing will happen. Add provideSchemaActions(…) where this schema is rendered.`
    );
  }
}

function reportResolveFailure(type: string, failure: unknown): void {
  if (isDevMode()) {
    console.warn(
      `Schema action "${type}" could not be resolved, so nothing happened. ` +
        `Register it with addEntryToRegistry("${type}", …) in an injector this node can reach.`,
      failure
    );
  }
}
